import React, { useState } from 'react';
import { Modal, Button, Form, Alert, ProgressBar } from 'react-bootstrap';
import { createVideo, manualUploadAudio } from '../services/api';

const ALLOWED_AUDIO_TYPES = ['audio/mpeg', 'audio/mp3', 'audio/wav', 'audio/webm', 'audio/ogg', 'audio/x-wav'];

const emptyForm = {
  name: '',
  googleDriveLink: '',
  lengthSeconds: '',
  codePrefix: 'FCS',
  code: '',
  narrator: 'Won',
};

const CreateVideoModal = ({ show, onHide, onCreateSuccess }) => {
  const [formData, setFormData] = useState(emptyForm);
  const [audioFile, setAudioFile] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState(null);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) {
      setAudioFile(null);
      return;
    }
    if (!ALLOWED_AUDIO_TYPES.includes(file.type)) {
      setError(`Invalid file type: ${file.type}. Allowed: mp3, wav, webm, ogg`);
      setAudioFile(null);
      return;
    }
    setError(null);
    setAudioFile(file);
  };

  const handleClose = () => {
    if (isSaving) return;
    setFormData(emptyForm);
    setAudioFile(null);
    setUploadProgress(0);
    setError(null);
    onHide();
  };

  const handleCreate = async () => {
    setError(null);

    const lengthSeconds = parseInt(formData.lengthSeconds, 10);
    if (!formData.name.trim()) {
      setError('Name is required');
      return;
    }
    if (!formData.googleDriveLink.trim()) {
      setError('Video link is required');
      return;
    }
    if (!lengthSeconds || lengthSeconds <= 0) {
      setError('Length (seconds) must be a positive number');
      return;
    }
    if (formData.codePrefix === 'ECS' && !formData.code.trim()) {
      setError('Code is required for ECS videos');
      return;
    }

    setIsSaving(true);
    try {
      const response = await createVideo({
        name: formData.name.trim(),
        googleDriveLink: formData.googleDriveLink.trim(),
        lengthSeconds,
        codePrefix: formData.codePrefix,
        code: formData.codePrefix === 'ECS' ? formData.code.trim() : undefined,
      });
      const newVideo = response.data;

      if (audioFile) {
        await manualUploadAudio(newVideo.id, formData.narrator, audioFile, (progress) => {
          setUploadProgress(progress);
        });
      }

      if (onCreateSuccess) {
        onCreateSuccess(newVideo);
      }
      handleClose();
    } catch (err) {
      console.error('Failed to create video:', err);
      setError(`Failed to create video: ${err.response?.data?.detail || err.message}`);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Modal show={show} onHide={handleClose} backdrop={isSaving ? 'static' : true}>
      <Modal.Header closeButton={!isSaving}>
        <Modal.Title>Create Video</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error && <Alert variant="danger">{error}</Alert>}

        <Form>
          <Form.Group className="mb-3">
            <Form.Label>Title</Form.Label>
            <Form.Control
              type="text"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              disabled={isSaving}
              placeholder="Video title"
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Video Link (Google Drive)</Form.Label>
            <Form.Control
              type="text"
              name="googleDriveLink"
              value={formData.googleDriveLink}
              onChange={handleInputChange}
              disabled={isSaving}
              placeholder="https://drive.google.com/..."
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Length (seconds)</Form.Label>
            <Form.Control
              type="number"
              name="lengthSeconds"
              value={formData.lengthSeconds}
              onChange={handleInputChange}
              disabled={isSaving}
              min="1"
              placeholder="e.g. 300"
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Code</Form.Label>
            <div>
              <Form.Check
                inline
                type="radio"
                label="FCS (auto-numbered)"
                name="codePrefix"
                value="FCS"
                checked={formData.codePrefix === 'FCS'}
                onChange={handleInputChange}
                disabled={isSaving}
              />
              <Form.Check
                inline
                type="radio"
                label="ECS"
                name="codePrefix"
                value="ECS"
                checked={formData.codePrefix === 'ECS'}
                onChange={handleInputChange}
                disabled={isSaving}
              />
            </div>
            {formData.codePrefix === 'FCS' ? (
              <Form.Text className="text-muted">
                Assigned automatically as FCS-&lt;id&gt; once the video is created.
              </Form.Text>
            ) : (
              <>
                <Form.Control
                  className="mt-2"
                  type="text"
                  name="code"
                  value={formData.code}
                  onChange={handleInputChange}
                  disabled={isSaving}
                  placeholder="e.g. ECS-42"
                />
                <Form.Text className="text-muted">
                  Must be unique. Avoid values shaped like FCS-&lt;number&gt;.
                </Form.Text>
              </>
            )}
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Narrator</Form.Label>
            <Form.Select
              name="narrator"
              value={formData.narrator}
              onChange={handleInputChange}
              disabled={isSaving}
            >
              <option value="Won">Won</option>
              <option value="Waan">Waan</option>
            </Form.Select>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Audio Description (optional)</Form.Label>
            <Form.Control
              type="file"
              accept="audio/*"
              onChange={handleFileChange}
              disabled={isSaving}
            />
            <Form.Text className="text-muted">
              Uploaded as-is for the selected narrator, same as manual upload (no sound-effect processing).
            </Form.Text>
            {audioFile && (
              <div className="mt-2">
                <strong>Selected:</strong> {audioFile.name} ({(audioFile.size / 1024 / 1024).toFixed(2)} MB)
              </div>
            )}
            {isSaving && audioFile && (
              <ProgressBar now={uploadProgress} label={`${uploadProgress}%`} animated striped className="mt-2" />
            )}
          </Form.Group>
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose} disabled={isSaving}>
          Cancel
        </Button>
        <Button variant="primary" onClick={handleCreate} disabled={isSaving}>
          {isSaving ? 'Creating...' : 'Create Video'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default CreateVideoModal;
